import {
  CUA_CLOUD_MACHINE_QUERY_KEY,
  CUA_CLOUD_MACHINE_SUMMARY_QUERY_KEY,
  DESKTOP_TARGETS_QUERY_KEY,
} from '@heidi/agents/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useIsCuaCloudEnabled } from '../../../flags';
import { cuaCloudClient } from '../cua-cloud/cuaCloud';

export type ResidentSetup = {
  provisioning: boolean;
  setUp: () => void;
};

const isNotProvisioned = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

/**
 * Inline "Set up" affordance for the desktop-target picker. CUA Cloud users
 * with no Resident yet get a provision action instead of an empty picker.
 *
 * Returns `undefined` to hide the affordance: flag off, machine state still
 * loading, or a machine already exists — the provision endpoint registers
 * the roster row itself, so an existing machine is already in the picker.
 */
export const useResidentSetup = (enabled: boolean): ResidentSetup | undefined => {
  const cuaCloudEnabled = useIsCuaCloudEnabled();
  const queryClient = useQueryClient();
  const active = enabled && cuaCloudEnabled;

  const machineQuery = useQuery({
    queryKey: CUA_CLOUD_MACHINE_SUMMARY_QUERY_KEY,
    queryFn: async () => (await cuaCloudClient.getMachine()).data,
    enabled: active,
    // 404 = not provisioned yet — the state this hook exists to surface.
    retry: (failureCount, error) => !isNotProvisioned(error) && failureCount < 2,
  });

  const provisionMutation = useMutation({
    mutationFn: () => cuaCloudClient.ensureMachine(),
    onSettled: async () => {
      // Base-key prefix covers both the summary probe and the settings page.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CUA_CLOUD_MACHINE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: DESKTOP_TARGETS_QUERY_KEY }),
      ]);
    },
  });

  if (!active) return undefined;
  if (provisionMutation.isPending) {
    return { provisioning: true, setUp: () => undefined };
  }
  if (!isNotProvisioned(machineQuery.error)) return undefined;
  return { provisioning: false, setUp: () => provisionMutation.mutate() };
};
